import { resolveDevtoolsIcon } from "./StringDevIconRegistry";

export type IconSize = 12 | 16 | 20;
export type ButtonModifier = "toggle" | "left" | "right";

export interface IconButtonOptions {
  icon: string;
  size?: IconSize;
  label: string;
  modifiers?: ButtonModifier[];
  /** Extra data-* attributes, e.g. { "data-open": "false" } */
  attrs?: Record<string, string>;
}

export interface PanelButtonOptions {
  icon: string;
  size?: IconSize;
  label: string;
  withLabel?: boolean;
  /** Extra data-* attributes */
  attrs?: Record<string, string>;
}

export function makeBadge(targetId?: string): HTMLDivElement {
  const el = document.createElement("div");
  el.setAttribute("data-stdg", "");
  el.setAttribute("data-stdg-badge", "");
  if (targetId) el.setAttribute("data-target-id", targetId);
  el.setAttribute("data-visible", "false");
  el.setAttribute("data-string-devtools-theme", "");
  return el;
}

export function makePanel(attrs?: Record<string, string>): HTMLDivElement {
  const el = document.createElement("div");
  el.setAttribute("data-stdg", "");
  el.setAttribute("data-stdg-panel", "");
  el.setAttribute("data-open", "false");
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  }
  el.addEventListener("pointerdown", (e) => e.stopPropagation());
  return el;
}

/**
 * Creates a panel header div.
 *   <div data-stdg-panel-header>
 */
export function makePanelHeader(): HTMLDivElement {
  const el = document.createElement("div");
  el.setAttribute("data-stdg-panel-header", "");
  return el;
}

/**
 * Creates a panel content div.
 *   <div data-stdg-panel-content>
 */
export function makePanelContent(attrs?: Record<string, string>): HTMLDivElement {
  const el = document.createElement("div");
  el.setAttribute("data-stdg-panel-content", "");
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  }
  return el;
}

/**
 * Creates a small icon button (header/toolbar use).
 *
 * Modifiers map to data-stdg-button-{modifier} attributes.
 * Default size is 16.
 *
 * @example
 * makeIconButton({ icon: "close", label: "Close panel", modifiers: ["right"] })
 */
export function makeIconButton(options: IconButtonOptions): HTMLButtonElement {
  const { icon, size = 16, label, modifiers = [], attrs } = options;
  const el = document.createElement("button");
  el.type = "button";
  el.setAttribute("data-stdg-button", "");
  el.setAttribute(`data-stdg-button-icon-${size}`, "");
  for (const mod of modifiers) {
    el.setAttribute(`data-stdg-button-${mod}`, "");
  }
  el.setAttribute("aria-label", label);
  el.setAttribute("title", label);
  el.innerHTML = resolveDevtoolsIcon(size, icon);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  }
  return el;
}

export function makeDeleteButton(label: string, attrs?: Record<string, string>): HTMLButtonElement {
  const el = document.createElement("button");
  el.type = "button";
  el.setAttribute("data-stdg-button", "");
  el.setAttribute("data-string-grid-list-delete", "");
  el.setAttribute("aria-label", label);
  el.setAttribute("title", label);
  el.innerHTML = resolveDevtoolsIcon(16, "minus");
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  }
  return el;
}

/**
 * Creates a panel-style button (wider, can include a text label next to the icon).
 *
 * @example
 * makePanelButton({ icon: "plus", label: "Add Layout Guide", withLabel: true })
 */
export function makePanelButton(options: PanelButtonOptions): HTMLButtonElement {
  const { icon, size = 16, label, withLabel = false, attrs } = options;
  const el = document.createElement("button");
  el.type = "button";
  el.setAttribute("data-stdg-button", "");
  el.setAttribute("data-stdg-button-toggle", "");
  el.setAttribute("data-stdg-panel-button", "");
  el.setAttribute(`data-stdg-panel-button-icon-${size}`, "");
  el.setAttribute("aria-label", label);
  el.setAttribute("title", label);
  el.innerHTML = withLabel
    ? `${resolveDevtoolsIcon(size, icon)}<span>${label}</span>`
    : resolveDevtoolsIcon(size, icon);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  }
  return el;
}
